<script lang="ts">
  import {
    GatDateCalendar,
    type GatDateCalendarProps
  } from '../gat-date-calendar'
  import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
  import {
    GatFormField,
    type GatFormFieldProps,
    type FormFieldValidationRule
  } from '../gat-form-field'
  import { cn } from '../../lib/utils'
  import { GatIcon } from '../gat-icon'
  import { Temporal } from '@js-temporal/polyfill'
  import type { ISODateTimeString } from '@gat-ship/types'
  import { useDatePicker } from './composables/useDatePicker'
  import { useTimeInput } from './composables/useTimeInput'
  import { useDateInputModel } from './composables/useModelValue'
  import { useInputValue } from './composables/useInputValue'
  import { useTimezone } from './composables/useTimezone'
  import { getCurrentTimeZoneId } from './composables/date-utils'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '../ui/dropdown-menu'

  export type GatDateEditProps = GatFormFieldProps<string | undefined> & {
    modelValue: ISODateTimeString | undefined
    /** If set, the input will automatically add 1 month to the date if the day is in the past (current month).
     * Only works if the user types a 2 digit input and blurs the input.
     */
    futureDate?: boolean
    /** The timezone of the input's value. */
    timeZone?: string
    /** If set, the input will automatically focus on mount. */
    autofocus?: boolean
    /** The type of the input. */
    type?: 'date' | 'datetime'
    /** The variant of the input.
     * @deprecated date-time-input is not finished yet, please use date-picker instead.
     */
    variant?: 'date-picker' | 'date-time-input' | 'calendar'
    /** If set, the input will automatically hide the details. */
    hideDetails?: boolean | 'auto'
    /** Anchor the date to the start or end of the day. */
    anchorTime?: 'midnight' | 'noon' | 'now'
    /** The dates that are disabled. */
    disabledDates?: GatDateCalendarProps['disabledDates']
    /** The ranges that are disabled. */
    disabledDateRanges?: GatDateCalendarProps['disabledRanges']
  }

  export type GatDateEditEmits = {
    (e: 'update:modelValue', value: ISODateTimeString | undefined): void
  }
  // const t is just here to not break the syntax highlighting.
  const t = undefined
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onMounted,
    ref,
    useTemplateRef,
    watch
  } from 'vue'
  import { useUser } from '@gat-ship/lib'

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(defineProps<GatDateEditProps>(), {
    showOptional: undefined,
    variant: 'date-picker',
    validateOn: 'input',
    hideDetails: 'auto',
    anchorTime: 'midnight'
  })

  const emit = defineEmits<GatDateEditEmits>()

  const user = useUser()

  const formField = useTemplateRef('form-field')

  const input = useTemplateRef('input')

  const loading = defineModel<boolean>('loading', {
    default: false,
    required: false
  })

  onMounted(async () => {
    if (props.autofocus) {
      await nextTick()
      input.value?.focus()
    }
  })

  const calendarOpen = ref(false)

  const { model, updateModel } = useDateInputModel(props, emit)

  const {
    inputValue,
    setInputValue,
    getUpdatedValuesFromUserInput,
    getInputValueFromModel
  } = useInputValue()

  const {
    localDateTime,
    timezoneDateTime,
    systemTimezone,
    invertedTimezone,
    selectedTimeZone,
    availableTimezones,
    setSelectedTimeZone
  } = useTimezone(props, updateModel)

  const { selectedDate, datePickerLabel, handleDateChange } = useDatePicker(
    {
      modelValue: model,
      timeZone: selectedTimeZone,
      anchorTime: props.anchorTime
    },
    updateModel
  )

  const { timeInputValue, handleTimeChange } = useTimeInput(
    { modelValue: model, timeZone: selectedTimeZone },
    updateModel
  )

  /** The locale of the date format. */
  const dateLocale = computed(() => {
    return (
      user.userPreferences.getUserPreferences().dateFormat ??
      Intl.DateTimeFormat().resolvedOptions().locale
    )
  })

  function onBlur(event: FocusEvent) {
    const value = (event.target as HTMLInputElement).value
    if (value === inputValue.value) {
      return
    }
    const result = getUpdatedValuesFromUserInput({
      value,
      locale: dateLocale.value,
      futureDate: props.futureDate ?? false,
      timeZone: props.timeZone || getCurrentTimeZoneId(),
      anchorTime: props.anchorTime
    })
    console.table({
      ...result,
      model: result.model?.toString(),
      futureDate: props.futureDate
    })
    updateModel(result.model)
    setInputValue(result.inputValue)
    return result
  }

  function getRules(): FormFieldValidationRule<string>[] {
    return []
  }

  const modelAsWeekDay = computed(() => {
    const _model = model.value
    if (!_model) {
      return undefined
    }
    // Safely parse the model value by first creating an Instant, then converting to ZonedDateTime
    const instant = Temporal.Instant.from(_model)
    const zonedDateTime = instant.toZonedDateTimeISO(selectedTimeZone.value)
    return zonedDateTime.toLocaleString('en-US', { weekday: 'long' })
  })

  // onMounted(async () => {
  //   await nextTick()
  //   const result = setModel(model.value, true)
  //   console.log('onMounted updated values...', result)
  // })

  // watch(
  //   () => model.value,
  //   async (value) => {
  //     const result = setModel(value, false)
  //     console.log('watch updated values...', result)
  //   }
  // )

  defineExpose({
    validate: formField.value?.validate(),
    loading
  })

  const isTestEnvironment = import.meta.env.MODE === 'test'
</script>

<template>
  <gat-form-field
    ref="form-field"
    v-bind="props"
    :value="model"
    :rules="getRules()"
    :hint="
      props.timeZone
        ? `This will be ${timezoneDateTime} in timezone (${invertedTimezone}) `
        : undefined
    "
    :class="cn({ 'border-none p-0': props.variant === 'calendar' })"
    @clear="emit('update:modelValue', undefined)"
  >
    <template
      v-if="variant === 'calendar'"
      #default="{ isValid, name, disabled, inputClass }"
    >
      <GatDateCalendar
        mode="single"
        :model-value="selectedDate"
        :disabled-dates="disabledDates"
        :disabled-ranges="disabledDateRanges"
        @update:model-value="handleDateChange"
      />
    </template>
    <template v-else #default="{ isValid, name, disabled, inputClass }">
      <div class="flex flex-col items-start space-y-2">
        <div class="flex w-full gap-2">
          <div class="flex w-full">
            <Popover v-model:open="calendarOpen" class="z-50">
              <PopoverTrigger as-child>
                <button
                  :disabled="disabled"
                  class="ring-offset-background focus-visible:ring-ring border-input bg-background hover:bg-accent hover:text-accent-foreground flex h-9 items-center justify-center rounded-md px-3 py-2 text-sm transition-colors focus-visible:z-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50"
                  :class="{
                    'grow justify-start gap-2 border':
                      props.variant === 'date-picker' ||
                      props.variant === 'calendar',
                    'gap-1 rounded-e-none rounded-s-lg border-b border-l border-t':
                      props.variant === 'date-time-input' ||
                      props.variant === 'calendar',
                    'border-red-500 text-red-500': !isValid
                  }"
                >
                  <gat-icon :icon="$icons.common.Calendar" class="size-3.5" />
                  <span v-if="props.variant === 'date-picker'">{{
                    datePickerLabel
                  }}</span>
                  <span
                    v-if="props.variant === 'date-picker' && props.timeZone"
                  >
                    ({{ selectedTimeZone }})
                  </span>
                  <gat-icon
                    v-if="props.variant === 'date-picker'"
                    :icon="$icons.common.collapsed"
                    class="ml-auto size-3.5 opacity-60"
                  ></gat-icon>
                </button>
              </PopoverTrigger>
              <PopoverContent as-child align="start">
                <GatDateCalendar
                  mode="single"
                  :model-value="selectedDate"
                  :disabled-dates="disabledDates"
                  :disabled-ranges="disabledDateRanges"
                  @update:model-value="handleDateChange"
                >
                  <template #footer>
                    <div
                      class="mt-2 flex items-center justify-center gap-2 border-t pt-2"
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          as-child
                          :disabled="!props.timeZone"
                        >
                          <div
                            class="flex w-full select-none items-center justify-center gap-2 rounded-sm px-2 py-1 text-sm"
                            :class="{
                              'hover:bg-accent hover:cursor-pointer':
                                props.timeZone,
                              'text-soft': !props.timeZone
                            }"
                          >
                            <gat-icon
                              :icon="$icons.common.Earth"
                              class="size-3.5"
                            />
                            {{ selectedTimeZone }}
                            <gat-icon
                              v-if="props.timeZone"
                              :icon="$icons.common.collapsed"
                              class="size-3.5"
                            />
                          </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent class="min-w-40">
                          <DropdownMenuItem
                            v-for="timezone in availableTimezones"
                            :key="timezone"
                            :disabled="timezone === selectedTimeZone"
                            :class="{
                              'hover:bg-accent hover:cursor-pointer':
                                timezone !== selectedTimeZone
                            }"
                            @click="setSelectedTimeZone(timezone)"
                          >
                            {{ timezone }}
                            <!-- <span
                              v-if="timezone === systemTimezone"
                              class="text-soft text-xs"
                            >
                              * Local time
                            </span> -->
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </template>
                </GatDateCalendar>
              </PopoverContent>
            </Popover>
            <input
              v-if="props.variant === 'date-time-input'"
              ref="input"
              type="text"
              :value="inputValue"
              @update:value="setInputValue"
              @blur="onBlur"
              :class="
                cn(inputClass, 'rounded-e-lg rounded-s-none px-2', props.class)
              "
            />
            <div
              v-if="props.variant === 'date-time-input'"
              class="text-soft absolute bottom-0 right-2.5 top-0 flex items-center text-sm"
            >
              {{ modelAsWeekDay }}
            </div>
          </div>
          <div v-if="props.type === 'datetime'">
            <input
              ref="time-input"
              :class="cn(inputClass, 'max-w-[75px] px-2', props.class)"
              type="text"
              :value="timeInputValue"
              @blur="handleTimeChange"
            />
          </div>
        </div>
      </div>
    </template>
    <!-- <template #append-inner>
      <div v-if="variant !== 'date-picker'" class="text-soft text-sm">
        {{ selectedDayInHumanReadable }}
      </div>
    </template> -->
  </gat-form-field>
  <input
    v-if="isTestEnvironment"
    :data-testid="props.dataTestid"
    :value="props.modelValue"
    @input="
      emit(
        'update:modelValue',
        ($event.target as HTMLInputElement).value as ISODateTimeString
      )
    "
  />
</template>
